// =============================================================
// theme — light/dark mode toggle
// =============================================================
//
// - Light is the default (no `data-theme` attribute = light tokens).
// - Persists to localStorage. An inline boot script in the HTML files
//   reads the same key BEFORE CSS paints, so the first frame already
//   shows the right theme — no flash of wrong colours.
// - `onThemeChange()` lets the toggle button (and any other UI) re-
//   render when the user flips the switch.

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'studio333:theme';
const ATTR = 'data-theme';

const isTheme = (s: string): s is Theme => s === 'light' || s === 'dark';

const subscribers = new Set<() => void>();

// ---------- Reading the initial theme (localStorage > default) ----------
// The inline boot script in index.html / admin.html already applies
// `data-theme="dark"` synchronously before paint. By the time this
// module loads, the DOM attribute is already correct — we just need
// to mirror it into JS state so `getTheme()` returns the right value.
export function detectInitial(): Theme {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute(ATTR);
    if (attr && isTheme(attr)) return attr;
  }
  return 'light';
}

// ---------- Subscription ----------
export function onThemeChange(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// ---------- State ----------
let _theme: Theme = 'light';

/** Apply the given theme to <html> and notify subscribers. */
function applyTheme(next: Theme): void {
  if (next === _theme) return;
  _theme = next;
  if (typeof document !== 'undefined') {
    if (next === 'light') {
      // Light is the default; drop the attribute so :root tokens win
      // without us having to redefine the light palette under [data-theme="light"].
      document.documentElement.removeAttribute(ATTR);
    } else {
      document.documentElement.setAttribute(ATTR, next);
    }
  }
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  subscribers.forEach(cb => {
    try { cb(); } catch (err) {
      console.error('[theme] subscriber threw during applyTheme:', err);
    }
  });
}

// ---------- Public API ----------
export function getTheme(): Theme {
  return _theme;
}

export function setTheme(next: Theme): void {
  applyTheme(next);
}

/** Flip light ↔ dark. Convenience for toggle buttons. */
export function toggleTheme(): Theme {
  applyTheme(_theme === 'dark' ? 'light' : 'dark');
  return _theme;
}

/** Read the persisted choice without applying it. Used by the inline
 *  boot script in index.html / admin.html. Falls back to 'light'. */
export function readPersistedTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isTheme(stored)) return stored;
  } catch { /* SSR / private mode */ }
  return 'light';
}

/** Initialise JS state from the DOM (which the inline boot script
 *  already set). Call once after the boot script has run. */
export function initTheme(): Theme {
  _theme = detectInitial();
  return _theme;
}
